(() => {
  'use strict';

  class CartBar extends HTMLElement {
    constructor() {
      super();
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundResetATC = this.resetATC.bind(this);
      this.boundOptionChange = this.handleOptionChange.bind(this);
      this.boundScroll = this.toggleScroll.bind(this);
      this.variantSyncPending = false;
    }

    connectedCallback() {
      this.section = this.closest('product-info');
      this.form = this.section?.querySelector('[data-product-form]');

      if (!this.section || !this.form) return;

      this.formWrapper =
        window.theme?.settings?.productPageSticky
          ? this.section.querySelector('[data-form-wrapper]')
          : this.form;

      this.image = this.querySelector('[data-cart-bar-image]');
      this.options = this.querySelector('[data-cart-bar-options]');
      this.qty = this.querySelector('[data-cart-bar-qty]');
      this.atcBtn = this.querySelector('[data-cart-bar-add-to-cart]');

      this.addEventListener('click', this.boundHandleClick);
      ['theme:cart:refresh', 'theme:cart-bar:reset-atc-animation'].forEach((eventName) => {
        document.addEventListener(eventName, this.boundResetATC);
      });

      this.initOptions();
      this.initQty();
      this.subVariant();
      this.initObserver();
    }

    resetATC() {
      this.atcBtn?.classList.remove('is-loading');

      if (!this.variantSyncPending) {
        this.atcBtn?.removeAttribute('disabled');
      }
    }

    handleClick(event) {
      if (event.target.closest('[data-scroll-to-form]')) {
        event.preventDefault();
        const productForm = this.section.querySelector('product-form');
        if (!productForm) return;

        productForm.setAttribute('tabindex', '-1');
        productForm.focus({ preventScroll: true });
        productForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      if (event.target.closest('[data-cart-bar-add-to-cart]')) {
        event.preventDefault();

        // The sticky selector updates the main PDP variant asynchronously.
        // Never submit while that synchronization is still in progress,
        // otherwise the previous variant ID can be added to the cart.
        if (this.variantSyncPending) return;

        const mainAddToCart = this.form.querySelector('[data-add-to-cart]');
        if (!mainAddToCart || mainAddToCart.hasAttribute('disabled')) return;

        this.atcBtn.classList.add('is-loading');
        this.atcBtn.setAttribute('disabled', true);

        if (this.form.requestSubmit) {
          this.form.requestSubmit(mainAddToCart);
        } else {
          mainAddToCart.click();
        }
      }
    }

    initOptions() {
      this.options?.querySelectorAll('[data-popout-input]').forEach((input) => {
        input.addEventListener('change', this.boundOptionChange);
      });
    }

    handleOptionChange(event) {
      const input = event.target;
      const optionValueId = input.getAttribute('data-option-value-id');

      this.updateOptionSwatch(input.closest('popout-select'), input.value);

      if (!optionValueId) return;

      const mainOption = this.section.querySelector(
        `variant-selects [data-option-value-id="${CSS.escape(optionValueId)}"]`
      );
      if (!mainOption) return;

      // product-info.js resolves the actual Shopify variant via fetch().
      // Lock the sticky ATC until the matching variantChange event confirms
      // that the main form's input[name="id"] has been updated.
      this.variantSyncPending = true;
      this.atcBtn?.setAttribute('disabled', true);
      this.atcBtn?.setAttribute('aria-busy', 'true');

      const clickEvent = new MouseEvent('click', {
        bubbles: true,
        cancelable: true
      });
      clickEvent.fromCartBar = true;

      if (mainOption.tagName === 'A' || mainOption.matches('input')) {
        mainOption.dispatchEvent(clickEvent);

        if (mainOption.matches('input')) {
          const changeEvent = new Event('change', { bubbles: true });
          changeEvent.fromCartBar = true;
          mainOption.dispatchEvent(changeEvent);
        }
      } else {
        mainOption
          .closest('label, a, [role="button"], .select-popout__option')
          ?.dispatchEvent(clickEvent);
      }
    }

    subVariant() {
      if (!window.subscribe || !window.theme?.PUB_SUB_EVENTS?.variantChange) return;

      this.unsub = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const sectionId = data?.sectionId;
        const belongsToThisProduct =
          !sectionId ||
          sectionId === this.section.dataset.section ||
          sectionId === this.section.dataset.originalSection;

        if (!belongsToThisProduct) return;

        // product-info.js publishes variantChange only after updateVariantInputs(),
        // so it is now safe for the sticky button to submit the main form.
        this.variantSyncPending = false;
        this.atcBtn?.removeAttribute('aria-busy');

        this.updateImage(data.variant);
        this.updateOptions(data.variant);
        this.updateBtn(data.variant);
        this.updatePrice(data.variant);
      });
    }

    updateImage(variant) {
      const image = this.image?.querySelector('img');
      const src = variant?.featured_media?.preview_image?.src;
      if (!image || !src) return;

      image.src = `${src}${src.includes('?') ? '&' : '?'}width=160`;
    }

    updateOptionSwatch(popout, selectedValue) {
      if (!popout) return;

      const swatch = popout.querySelector('[data-cart-bar-current-swatch]');
      const label = popout.dataset.cartBarOptionLabel || '';
      const selectedOption = selectedValue
        ? Array.from(popout.querySelectorAll('[data-popout-option]')).find(
            (option) => option.dataset.value === selectedValue
          )
        : popout.querySelector('[data-popout-option][aria-current="true"]');

      const selectedItem = selectedOption?.closest('li');
      const resolvedValue = selectedValue || selectedOption?.dataset.value || '';

      if (swatch) {
        const swatchValue = selectedItem?.dataset.cartBarSwatch || '';
        if (swatchValue) {
          swatch.style.setProperty('--cart-bar-swatch', swatchValue);
          swatch.hidden = false;
        } else {
          swatch.hidden = true;
        }
      }

      const toggle = popout.querySelector('[data-popout-toggle]');
      if (toggle && label && resolvedValue) {
        toggle.setAttribute('aria-label', `${label}: ${resolvedValue}`);
      }
    }

    updateOptions(variant) {
      if (!this.options || !variant?.options) return;

      this.options.querySelectorAll('popout-select').forEach((popout) => {
        const input = popout.querySelector('[data-popout-input]');
        const optionIndex = parseInt(input?.dataset.index?.replace('option', '') || '', 10);
        if (!input || Number.isNaN(optionIndex)) return;

        const selectedValue = variant.options[optionIndex - 1];
        const displayText = popout.querySelector('[data-popout-text]');

        popout.querySelectorAll('[data-popout-option]').forEach((option) => {
          const value = option.dataset.value;
          const isSelected = value === selectedValue;
          const item = option.closest('li');

          if (isSelected && item) {
            if (displayText) displayText.textContent = value;
            input.value = value;

            if (item.dataset.optionValueId) {
              input.setAttribute('data-option-value-id', item.dataset.optionValueId);
            }
            if (item.dataset.productUrl) {
              input.setAttribute('data-product-url', item.dataset.productUrl);
            }

            popout
              .querySelector('.select-popout__item--current')
              ?.classList.remove('select-popout__item--current');
            item.classList.add('select-popout__item--current');

            popout
              .querySelector('[aria-current="true"]')
              ?.removeAttribute('aria-current');
            option.setAttribute('aria-current', 'true');
          }

          const optionValueId = item?.dataset.optionValueId;
          if (!optionValueId) return;

          const mainOption = this.section.querySelector(
            `variant-selects [data-option-value-id="${CSS.escape(optionValueId)}"]`
          );
          if (!mainOption) return;

          const unavailable = Boolean(
            mainOption.closest('.sold-out, .unavailable') ||
              mainOption.classList.contains('unavailable') ||
              mainOption.disabled
          );
          option.classList.toggle('unavailable', unavailable);
        });

        this.updateOptionSwatch(popout, selectedValue);
      });
    }

updateBtn(variant) {
  const button = this.atcBtn;
  const text = this.querySelector('[data-add-to-cart-text]');
  if (!button || !text || !variant) return;

  const mainText = this.form?.querySelector(
    '[data-add-to-cart] [data-add-to-cart-text]'
  );

  const resolvedText = mainText?.textContent?.trim();

  if (resolvedText) {
    text.textContent = resolvedText;
  } else {
    const fallbackText = variant.available
      ? window.theme?.strings?.add_to_cart
      : window.theme?.strings?.sold_out;

    if (fallbackText) {
      text.textContent = fallbackText;
    }
  }

  button.toggleAttribute('disabled', !variant.available);
}

    updatePrice(variant) {
      const price = this.querySelector('[data-product-price]');
      if (!price || !variant) return;

      const formatMoney = (amount) =>
        window.theme.formatMoney(
          amount,
          window.theme?.settings?.currency_code_enable
            ? window.theme.moneyWithCurrencyFormat
            : window.theme.moneyFormat
        );

      price.innerHTML =
        variant.price === 0 ? window.theme?.strings?.free || 'Free' : formatMoney(variant.price);

      const onSale = variant.compare_at_price > variant.price;
      price.classList.toggle('product__price--sale', onSale);

      const comparePrice = this.querySelector('.product__price--strike');
      if (comparePrice) {
        comparePrice.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        comparePrice.classList.toggle('hidden', !onSale);
      }
    }

    initQty() {
      if (!this.qty) return;

      const barQty = this.qty.querySelector('[data-quantity-field]');
      const mainQty = Array.from(this.section.querySelectorAll('[data-quantity-holder]'))
        .find((holder) => !this.contains(holder))
        ?.querySelector('[data-quantity-field]');

      if (!barQty || !mainQty) return;

      barQty.value = mainQty.value;
      this.qtySync = false;

      const sync = (source, target) => {
        if (this.qtySync) return;
        this.qtySync = true;
        target.value = source.value;
        target.dispatchEvent(new Event('change', { bubbles: true }));
        this.qtySync = false;
      };

      this.syncMain = () => sync(mainQty, barQty);
      this.syncBar = () => sync(barQty, mainQty);

      [mainQty, barQty].forEach((input) => {
        ['input', 'change'].forEach((eventName) => {
          input.addEventListener(eventName, input === mainQty ? this.syncMain : this.syncBar);
        });
      });

      this.mainQtyRef = mainQty;
      this.barQtyRef = barQty;
    }

    initObserver() {
      if ('IntersectionObserver' in window && this.formWrapper) {
        this.obs = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle(
                'is-visible',
                !entry.isIntersecting && entry.boundingClientRect.top < 0
              );
            });
          },
          { rootMargin: '0px', threshold: 0 }
        );
        this.obs.observe(this.formWrapper);
      } else {
        document.addEventListener('theme:scroll', this.boundScroll);
      }
    }

    toggleScroll() {
      if (!this.formWrapper) return;
      this.classList.toggle(
        'is-visible',
        window.scrollY > this.formWrapper.offsetTop + this.formWrapper.offsetHeight
      );
    }

    disconnectedCallback() {
      ['theme:cart:refresh', 'theme:cart-bar:reset-atc-animation'].forEach((eventName) => {
        document.removeEventListener(eventName, this.boundResetATC);
      });

      if (this.obs) {
        this.obs.disconnect();
      } else {
        document.removeEventListener('theme:scroll', this.boundScroll);
      }

      this.unsub?.();
      this.removeEventListener('click', this.boundHandleClick);

      this.options?.querySelectorAll('[data-popout-input]').forEach((input) => {
        input.removeEventListener('change', this.boundOptionChange);
      });

      if (this.mainQtyRef) {
        ['input', 'change'].forEach((eventName) => {
          this.mainQtyRef.removeEventListener(eventName, this.syncMain);
        });
      }

      if (this.barQtyRef) {
        ['input', 'change'].forEach((eventName) => {
          this.barQtyRef.removeEventListener(eventName, this.syncBar);
        });
      }
    }
  }

  if (!customElements.get('cart-bar')) {
    customElements.define('cart-bar', CartBar);
  }
})();
